import { useLocation, type Location } from 'react-router'

interface WithinPageState {
  readonly isWithinPage: true
  /** The key of the move that opened the page, kept across moves within it. */
  readonly visitKey: string
}

function withinPageState(state: unknown): WithinPageState | null {
  const candidate = state as Partial<WithinPageState> | null | undefined
  return candidate?.isWithinPage === true && typeof candidate.visitKey === 'string' ? (candidate as WithinPageState) : null
}

/**
 * The state for a move that stays on the page, such as to another of its tabs: the page's own
 * state is kept, the unsaved changes prompt lets it through, and the visit stays the same.
 */
export function withinPage(location: Pick<Location, 'state' | 'key'>): object {
  const state: unknown = location.state
  const own = typeof state === 'object' && state !== null ? state : {}
  return { ...own, isWithinPage: true, visitKey: pageVisitKeyOf(location) }
}

/** Whether a move's state says it stays on the page. */
export function isWithinPage(state: unknown): boolean {
  return withinPageState(state) !== null
}

function pageVisitKeyOf(location: Pick<Location, 'state' | 'key'>): string {
  return withinPageState(location.state)?.visitKey ?? location.key
}

/** One visit to the page: the same across moves within it, such as a switch of its tabs. */
export function usePageVisitKey(): string {
  return pageVisitKeyOf(useLocation())
}
